import { useMutation, useQueryClient } from "@tanstack/react-query";

import { DealerAuctionDTO } from "@/src/types/types";

import { DealerQueryKeys, toggleFavourite } from "./dealerFunctions";

export const usePostFavourite = (queryKey: string) => {
  const queryClient = useQueryClient();

  /**
   * Updates `DealerQueryKeys.Auctions` state if `auctionId` found.
   * @param auctionId
   */
  const updateAuctionsState = (
    currentArray: DealerAuctionDTO[],
    auctionId: number
  ) => {
    const updatedArray = [...currentArray];
    const oldAuctionIndex = updatedArray.findIndex(
      ({ id }) => auctionId === id
    );
    if (oldAuctionIndex !== -1) {
      const oldAuction = updatedArray[oldAuctionIndex];
      updatedArray[oldAuctionIndex] = {
        ...oldAuction,
        isFavourite: !oldAuction.isFavourite
      };
    }
    return updatedArray;
  };
  return useMutation({
    mutationFn: toggleFavourite,
    onMutate: async ({ auctionId }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: [queryKey] });
      await queryClient.cancelQueries({ queryKey: [DealerQueryKeys.AUCTION] });
      await queryClient.cancelQueries({ queryKey: [DealerQueryKeys.AUCTIONS] });
      await queryClient.cancelQueries({
        queryKey: [DealerQueryKeys.MY_ACTIVE_AUCTIONS]
      });

      // Snapshot the previous values for rollback
      const previousValue = queryClient.getQueryData([queryKey]) as
        | DealerAuctionDTO[]
        | DealerAuctionDTO
        | undefined;

      const previousAuctions = queryClient.getQueryData([
        DealerQueryKeys.AUCTIONS
      ]) as DealerAuctionDTO[] | undefined;

      const previousMyAuctions = queryClient.getQueryData([
        DealerQueryKeys.MY_ACTIVE_AUCTIONS
      ]) as DealerAuctionDTO[] | undefined;

      const previousAuction = queryClient.getQueryData([
        DealerQueryKeys.AUCTION,
        auctionId
      ]) as DealerAuctionDTO | undefined;

      // Optimistically update the primary query key
      if (previousValue && Array.isArray(previousValue)) {
        const newAuctions = updateAuctionsState(previousValue, auctionId);
        queryClient.setQueryData([queryKey], newAuctions);
      } else if (previousValue?.id === auctionId) {
        queryClient.setQueryData([queryKey], {
          ...previousValue,
          isFavourite: !previousValue.isFavourite
        });
      }

      // Optimistically update other query keys that might contain this auction
      if (previousAuctions) {
        const newAuctions = updateAuctionsState(previousAuctions, auctionId);
        queryClient.setQueryData([DealerQueryKeys.AUCTIONS], newAuctions);
      }

      if (previousMyAuctions) {
        const newMyAuctions = updateAuctionsState(
          previousMyAuctions,
          auctionId
        );
        queryClient.setQueryData(
          [DealerQueryKeys.MY_ACTIVE_AUCTIONS],
          newMyAuctions
        );
      }

      if (previousAuction?.id === auctionId) {
        queryClient.setQueryData([DealerQueryKeys.AUCTION, auctionId], {
          ...previousAuction,
          isFavourite: !previousAuction.isFavourite
        });
      }

      return {
        previousValue,
        previousAuctions,
        previousMyAuctions,
        previousAuction
      };
    },
    onError: (error, variables, context) => {
      // Rollback optimistic updates on error
      if (context?.previousValue) {
        queryClient.setQueryData([queryKey], context.previousValue);
      }
      if (context?.previousAuctions) {
        queryClient.setQueryData(
          [DealerQueryKeys.AUCTIONS],
          context.previousAuctions
        );
      }
      if (context?.previousMyAuctions) {
        queryClient.setQueryData(
          [DealerQueryKeys.MY_ACTIVE_AUCTIONS],
          context.previousMyAuctions
        );
      }
      if (context?.previousAuction) {
        queryClient.setQueryData(
          [DealerQueryKeys.AUCTION, variables.auctionId],
          context.previousAuction
        );
      }
    },
    // Triggered on both onSuccess & onError
    onSettled: () => {
      // Invalidate queries to ensure we have the latest data from server
      queryClient.invalidateQueries({ queryKey: [DealerQueryKeys.AUCTION] });
      queryClient.invalidateQueries({ queryKey: [DealerQueryKeys.AUCTIONS] });
      queryClient.invalidateQueries({
        queryKey: [DealerQueryKeys.AUCTIONS_SUMMARY]
      });
      queryClient.invalidateQueries({
        queryKey: [DealerQueryKeys.MY_ACTIVE_AUCTIONS]
      });
    }
  });
};
